import type { ReactNode } from 'react';
import { Box, Button, Group, Stack, Text } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { BadgeBlock } from '../../../components/BadgeBlock';
import type { RoleOption } from './roleEditorLogic';

// ── Shared components ──────────────────────────────────────────────────────────

/** Icon-only close button used in modal headers */
export function ModalCloseButton({ onClick }: Readonly<{ onClick: () => void }>) {
  return (
    <Button
      variant="white"
      p={0}
      size="compact-md"
      style={{
        '--button-bg': 'transparent',
        '--button-hover': 'var(--bg-subtle)',
        '--button-color': 'var(--text-subtle)',
        '--button-hover-color': 'var(--text-default)',
        aspectRatio: 1,
        flexShrink: 0,
      }}
      onClick={onClick}
    >
      <IconX size={18} />
    </Button>
  );
}

/** A section row: title + description on left, arbitrary control on right. Bottom-bordered. */
export function RoleSection({
  title,
  desc,
  children,
  noBorder = false,
}: Readonly<{
  title: string;
  desc: string;
  children: ReactNode;
  noBorder?: boolean;
}>) {
  return (
    <Box py={15} style={noBorder ? undefined : { borderBottom: '1px solid var(--border-subtle)' }}>
      <Group justify="space-between" gap={16} wrap="nowrap" align="flex-start">
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Text fz="sm" fw={700} lh="normal">{title}</Text>
          <Text fz="xs" c="var(--text-muted)" lh="normal" maw={300}>{desc}</Text>
        </Stack>
        {children}
      </Group>
    </Box>
  );
}

/** Segmented selector: None / Viewer / Admin (or similar) */
export function RoleSegControl({
  options,
  value,
  onChange,
  disabled = false,
}: Readonly<{
  options: string[];
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}>) {
  return (
    <Box
      aria-disabled={disabled}
      style={{
        display: 'inline-flex',
        flexShrink: 0,
        background: 'var(--bg-subtle)',
        border: '1px solid var(--border-default)',
        borderRadius: 9,
        padding: 3,
        gap: 2,
        ...(disabled ? { opacity: 0.45, pointerEvents: 'none' as const } : {}),
      }}
    >
      {options.map(opt => {
        const active = value === opt;
        return (
          <Button
            key={opt}
            variant="subtle"
            size="compact-sm"
            fz="xs"
            bdrs={6}
            onClick={() => onChange(opt)}
            style={{
              '--button-hover-color': 'var(--text-default)',
              '--button-color': 'var(--text-muted)',
              background: active ? 'var(--bg-surface)' : 'none',
              color: active ? 'var(--accent-text)' : undefined,
              boxShadow: active ? 'var(--shadow-sm)' : 'none',
            }}
          >
            {opt.charAt(0).toUpperCase() + opt.slice(1)}
          </Button>
        );
      })}
    </Box>
  );
}

/** Selected-roles preview box */
export function SelectedRoles({
  selected,
  available,
  emptyText = 'No roles assigned — this user has no access.',
}: Readonly<{
  selected: string[];
  available: RoleOption[];
  emptyText?: string;
}>) {
  return (
    <Box
      mt={16}
      p="13px 14px"
      style={{
        background: 'var(--bg-subtle)',
        border: '1px solid var(--border-default)',
        borderRadius: 10,
      }}
    >
      <Text fz={11} fw={700} tt="uppercase" c="var(--text-subtle)" mb={9} style={{ letterSpacing: '0.5px' }}>
        Selected roles
      </Text>
      {selected.length === 0 ? (
        <Text fz="xs" c="var(--text-subtle)">{emptyText}</Text>
      ) : (
        <Stack gap={7}>
          {selected.map(slug => {
            const name = available.find(r => r.slug === slug)?.name;
            // Skip the name when it's just the capitalised slug (e.g. member -> "Member").
            const showName = name && name.toLowerCase() !== slug.toLowerCase();
            return (
              <Group key={slug} gap={9} wrap="nowrap">
                <BadgeBlock
                  color="var(--accent-text)"
                  py={2} h="auto" fz={12} fw={600} tt="none" bdrs={6}
                  bg="var(--brand-soft)"
                >
                  {slug}
                </BadgeBlock>
                {showName && <Text fz="xs" c="var(--text-muted)">{name}</Text>}
              </Group>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
